/*
 * pogi-cross-sell — §5.5 PDP "Complete the routine" cross-sell.
 * Ports window.PogiCrossSell behaviour onto Horizon's native cart + variant
 * events. The anchor (page product) is never a buyable tile; complements toggle;
 * any complement already in the cart locks as "In cart ✓" and is excluded from
 * the add + the bundle total. The add posts the hero + picked add-ons in ONE
 * /cart/add.js (include-hero; drops to add-on-only when the hero's already in
 * cart), opens the drawer, and re-renders from the live cart. The discount is a
 * DISPLAY preview only — the real cut is a Shopify automatic discount (§5.5c).
 */
import { CartUpdateEvent, ThemeEvents } from '@theme/events';

class PogiCrossSell extends HTMLElement {
  connectedCallback() {
    this.discount = (parseFloat(this.dataset.discount) || 0) / 100;
    this.freeShip = parseInt(this.dataset.freeShipCents, 10) || 2500; // cents
    this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
    this.heroVariantId = String(this.dataset.heroVariantId || '');
    this.heroProductId = String(this.dataset.heroProductId || '');
    this.heroPrice = parseInt(this.dataset.heroPrice, 10) || 0;
    this.heroImg = this.dataset.heroImg || '';

    // variant → anchor image map (each format/scent has its own image), so the
    // hero tile + bundle-bar chip can follow the buy box's variant:update.
    this.heroImgs = {};
    const imgScript = this.querySelector('[data-xs-hero-imgs]');
    if (imgScript) { try { this.heroImgs = JSON.parse(imgScript.textContent); } catch (e) { /* keep featured */ } }

    // i18n: JA overrides for the JS-rendered add-button states, from the
    // [data-xs-i18n] blob the section renders via `| t`. EN fallbacks stay inline.
    this.I = {};
    const i18nEl = this.querySelector('[data-xs-i18n]');
    if (i18nEl) { try { this.I = JSON.parse(i18nEl.textContent); } catch (e) { /* keep EN fallbacks */ } }
    // EVERY read falls back to the exact English literal that used to be hardcoded,
    // so a missing key can never change the EN site. [[count]]/[[amount]] placeholders
    // (not {{ }}) so Liquid's t-filter never eats them.
    this.t = (k, fb, vars) => {
      let s = (this.I && this.I[k]) || fb;
      if (vars) for (const key of Object.keys(vars)) s = s.split('[[' + key + ']]').join(String(vars[key]));
      return s;
    };
    this.anchorImgEl = this.querySelector('[data-xs-anchor-img]');

    this.tiles = Array.from(this.querySelectorAll('[data-xs-tile]'));
    this.picked = new Set();
    this.cartVariantIds = new Set();
    this.cartProductIds = new Set();

    // lead complement(s) pre-selected
    this.tiles.forEach((t) => { if (t.dataset.lead === '1') this.picked.add(t.dataset.variantId); });

    this.addEventListener('click', (e) => {
      if (e.target.closest('[data-xs-add]')) { this.add(); return; }
      const tile = e.target.closest('[data-xs-tile]');
      if (tile && !tile.classList.contains('is-owned')) {
        const id = tile.dataset.variantId;
        if (this.picked.has(id)) this.picked.delete(id); else this.picked.add(id);
        this.render();
      }
    });

    // Variant binding — follow the buy box's native variant:change (§5).
    document.addEventListener('variant:update', (e) => {
      const v = e.detail && e.detail.resource;
      const pid = e.detail && e.detail.data && e.detail.data.productId;
      if (!v || String(pid) !== this.heroProductId) return;
      this.heroVariantId = String(v.id);
      if (typeof v.price === 'number') this.heroPrice = v.price;
      const meta = this.querySelector('[data-xs-hero-meta]');
      //   glue mirrors the pogi-noorphan Liquid render of this line
      if (meta) meta.textContent = (v.title ? v.title.replace(/ (?=\S+$)/, ' ') + ' · ' : '') + this.money(this.heroPrice);
      // Swap the anchor tile image (and thus the bundle-bar chip) to the selected
      // variant's image so the picture stays in step with the variant-aware text.
      const newImg = this.heroImgs[this.heroVariantId];
      if (newImg) { this.heroImg = newImg; if (this.anchorImgEl) this.anchorImgEl.src = newImg; }
      this.render();
    });

    // Stay in sync when the cart changes ANYWHERE (e.g. the shopper removes a
    // complement from the drawer): re-sync owned state + re-render so a removed
    // tile unlocks and is clickable again. Skip our own add (already handled).
    document.addEventListener(ThemeEvents.cartUpdate, (e) => {
      if (e.detail && e.detail.sourceId === 'pogi-cross-sell') return;
      this.syncCart().then(() => this.render());
    });

    this.syncCart().then(() => this.render());
  }

  async syncCart() {
    try {
      const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      const cart = await res.json();
      this.cartVariantIds = new Set((cart.items || []).map((i) => String(i.variant_id)));
      this.cartProductIds = new Set((cart.items || []).map((i) => String(i.product_id)));
      this.cart = cart;
    } catch (e) { /* leave empty — block still renders, just not cart-aware */ }
  }

  heroInCart() {
    return this.cartVariantIds.has(this.heroVariantId) || this.cartProductIds.has(this.heroProductId);
  }
  owned(t) {
    // variant-only complements (an ALT format of the page's OWN product, e.g.
    // Refill Rolls offered on a Tie-Handle bag) must match the exact variant —
    // matching the product would falsely lock the tile whenever ANY variant of
    // that product is already in the cart (the anchor always is, post-add).
    if (t.dataset.variantOnly === '1') return this.cartVariantIds.has(t.dataset.variantId);
    return this.cartVariantIds.has(t.dataset.variantId) || this.cartProductIds.has(t.dataset.productId);
  }
  // A complement gated to HIDE for specific hero variants (data-hide-for list).
  hiddenForHero(t) {
    const list = t.dataset.hideFor;
    if (!list) return false;
    return list.split(',').map((s) => s.trim()).filter(Boolean).includes(String(this.heroVariantId));
  }
  // A complement gated to SHOW only for an allow-list of hero variants
  // (data-show-for) — e.g. the scented dispenser shows ONLY on scented refill
  // rolls; the alt bag format shows ONLY on Jumbo / Tie-Handle. Empty = always.
  shownForHero(t) {
    const list = t.dataset.showFor;
    if (!list) return true;
    return list.split(',').map((s) => s.trim()).filter(Boolean).includes(String(this.heroVariantId));
  }
  // Net visibility for the current hero variant.
  visible(t) { return !this.hiddenForHero(t) && this.shownForHero(t); }

  money(cents) {
    const amt = (cents || 0) / 100;
    const grp = (s) => s.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return this.moneyFormat
      .replace(/\{\{\s*amount\s*\}\}/g, grp(amt.toFixed(2)))
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, grp(Math.round(amt).toString()))
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, grp(amt.toFixed(2)))
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, grp(Math.round(amt).toString()));
  }

  setText(sel, val) { const el = this.querySelector(sel); if (el) el.textContent = val; }

  render() {
    const heroInCart = this.heroInCart();
    const toAdd = [];
    const ownedAddons = [];

    this.tiles.forEach((t) => {
      // Variant-gated complement: hide when the page's selected variant already
      // includes it (the 50-roll +2 dispensers bundle hides the Dispenser tile).
      const vis = this.visible(t);
      t.style.display = vis ? '' : 'none';
      if (!vis) { this.picked.delete(t.dataset.variantId); t.classList.remove('is-on'); t.setAttribute('aria-pressed', 'false'); return; }
      const own = this.owned(t);
      // Once owned, drop it from `picked` so deleting it from the cart returns
      // the tile to UNCHECKED (Add) — not re-armed as a selected add-on.
      if (own) this.picked.delete(t.dataset.variantId);
      const on = this.picked.has(t.dataset.variantId) && !own;
      t.classList.toggle('is-owned', own);
      t.classList.toggle('is-on', on);
      t.setAttribute('aria-pressed', String(on || own));
      t.disabled = own;
      this.setTileState(t, own ? this.t('tile_in_cart', 'In cart ✓') : on ? this.t('tile_in_kit', 'In your kit') : this.t('tile_add', 'Add'));
      if (own) ownedAddons.push(t); else if (on) toAdd.push(t);
    });

    const heroChip = this.querySelector('[data-xs-hero-chip]');
    if (heroChip) heroChip.textContent = heroInCart ? this.t('hero_in_cart', 'In your cart') : this.t('hero_this_item', 'This item');
    // Checkmark only in the in-cart state; "This item" (no ✓) when not in cart.
    const heroCheck = this.querySelector('[data-xs-hero-check]');
    if (heroCheck) heroCheck.hidden = !heroInCart;

    const addonsList = toAdd.reduce((sum, t) => sum + (parseInt(t.dataset.price, 10) || 0), 0);
    const addonsKit = Math.round(addonsList * (1 - this.discount));
    const save = addonsList - addonsKit;
    const heroAdd = !heroInCart;
    const addCount = (heroAdd ? 1 : 0) + toAdd.length;
    const displayTotal = (heroAdd ? this.heroPrice : 0) + addonsKit;
    const displayStruck = (heroAdd ? this.heroPrice : 0) + addonsList;
    const everythingInCart = heroInCart && this.tiles.filter((t) => this.visible(t)).every((t) => this.owned(t));
    const pct = Math.round(this.discount * 100);

    // bar label
    let barLabel;
    if (everythingInCart) barLabel = this.t('bar_routine', 'Your routine');
    else if (heroInCart) barLabel = toAdd.length === 0 ? this.t('bar_addons', 'Your add-ons') : this.t('bar_addons_count', 'Add-ons · ' + toAdd.length + ' item' + (toAdd.length > 1 ? 's' : ''), { count: toAdd.length });
    else barLabel = this.t('bar_kit_count', 'Your kit · ' + addCount + ' item' + (addCount > 1 ? 's' : ''), { count: addCount });
    this.setText('[data-xs-barlabel]', barLabel);

    // prices
    const prices = this.querySelector('[data-xs-prices]');
    if (prices) {
      if (addCount === 0) {
        prices.innerHTML = '<span class="pogi-routine__bar-hint">' + (everythingInCart ? this.t('hint_all_in_cart', "Your whole routine’s in the cart ✓") : this.t('hint_add_essentials', 'Add the essentials to complete your routine.')) + '</span>';
      } else {
        let h = '<span class="pogi-routine__bar-total">' + this.money(displayTotal) + '</span>';
        if (save > 0) h += '<span class="pogi-routine__bar-struck">' + this.money(displayStruck) + '</span><span class="pogi-routine__bar-save">' + this.t('save_caps', 'SAVE ' + this.money(save), { amount: this.money(save) }) + '</span>';
        prices.innerHTML = h;
      }
    }

    // chips: hero + selected/owned add-ons
    const chips = this.querySelector('[data-xs-chips]');
    if (chips) {
      const imgs = [this.heroImg].concat(toAdd.map((t) => t.dataset.img), ownedAddons.map((t) => t.dataset.img)).filter(Boolean);
      chips.innerHTML = imgs.map((src, i) => (i ? '<span class="x-plus">+</span>' : '') + '<span class="x-chip"><img src="' + src + '" alt=""></span>').join('');
    }

    // add button — ONE cart evaluation drives label + disabled, in step with the
    // bar label/hint above. "In your cart" only when the whole routine is owned;
    // never while add-ons remain (that was the desync bug).
    const btn = this.querySelector('[data-xs-add]');
    if (btn) {
      let label, disabled;
      if (everythingInCart) {
        label = this.t('btn_in_cart', 'In your cart');
        disabled = true;
      } else if (heroInCart) {
        if (toAdd.length === 0) { label = this.t('btn_pick_addon', 'Pick an add-on'); disabled = true; }
        else { label = this.t('btn_add_to_order', 'Add ' + toAdd.length + ' to your order', { count: toAdd.length }); disabled = false; }
      } else {
        label = toAdd.length === 0 ? this.t('btn_add_to_cart', 'Add to cart') : this.t('btn_add_count', 'Add ' + addCount + ' items to cart', { count: addCount });
        disabled = false;
      }
      btn.textContent = label;
      btn.disabled = disabled;
    }

    this.setText('[data-xs-freeship]', this.t('ships_free', 'Your order ships free.'));
  }

  setTileState(tile, txt) { const el = tile.querySelector('[data-xs-state]'); if (el) el.textContent = txt; }

  async add() {
    const heroAdd = !this.heroInCart();
    const items = [];
    if (heroAdd && this.heroVariantId) items.push({ id: parseInt(this.heroVariantId, 10), quantity: 1 });
    this.tiles.forEach((t) => {
      if (!this.owned(t) && this.visible(t) && this.picked.has(t.dataset.variantId)) items.push({ id: parseInt(t.dataset.variantId, 10), quantity: 1 });
    });
    const valid = items.filter((i) => i.id);
    if (!valid.length) return;

    const btn = this.querySelector('[data-xs-add]');
    const prev = btn ? btn.textContent : '';
    if (btn) { btn.disabled = true; btn.textContent = this.t('adding', 'Adding…'); }
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: valid }),
      });
      if (!res.ok) throw new Error('add failed');
      await this.syncCart();
      // Tell Horizon's header bubble + slide-out drawer to refresh. MUST be the
      // class-based CartUpdateEvent (name = ThemeEvents.cartUpdate) — a plain
      // CustomEvent('cart:update') is what nothing listens for (the sync bug).
      document.dispatchEvent(new CartUpdateEvent(this.cart || {}, 'pogi-cross-sell', {
        source: 'pogi-cross-sell',
        itemCount: this.cart ? this.cart.item_count : undefined,
      }));
      const drawer = document.querySelector('cart-drawer-component');
      if (drawer && typeof drawer.showDialog === 'function') drawer.showDialog();
      this.render();
    } catch (e) {
      if (btn) { btn.disabled = false; btn.textContent = prev || this.t('try_again', 'Try again'); }
    }
  }
}

if (!customElements.get('pogi-cross-sell')) {
  customElements.define('pogi-cross-sell', PogiCrossSell);
}
